// In-memory cache for common lookup data (users, departments)
// Prevents duplicate and redundant API calls across pages and modals

let usersCache = {
  data: null,
  timestamp: 0,
  userId: null,
  tenantId: null,
};
let inFlightUsersPromise = null;

let deptsCache = {
  data: null,
  timestamp: 0,
  userId: null,
  tenantId: null,
};
let inFlightDeptsPromise = null;

const TTL_MS = 3 * 60 * 1000; // 3 minutes

const extractIds = (user) => {
  if (!user) return { userId: null, tenantId: null };
  const userId = user._id ? user._id.toString() : user.id ? user.id.toString() : null;
  const tenant = user.tenantId;
  const tenantId = tenant ? (typeof tenant === 'object' && tenant._id ? tenant._id.toString() : tenant.toString()) : null;
  return { userId, tenantId };
};

// ─── Users Cache ─────────────────────────────────────────────────────────────
export const fetchUsersCached = async (userAPI, user, params = { limit: 100 }) => {
  if (!user || !userAPI?.getAll) return [];
  const { userId, tenantId } = extractIds(user);
  if (!userId) return [];

  const now = Date.now();
  if (
    usersCache.data &&
    usersCache.userId === userId &&
    usersCache.tenantId === tenantId &&
    now - usersCache.timestamp < TTL_MS
  ) {
    return usersCache.data;
  }

  if (inFlightUsersPromise) {
    return inFlightUsersPromise;
  }

  inFlightUsersPromise = userAPI.getAll(params)
    .then((res) => {
      const list = res?.data?.data?.users || res?.data?.users || [];
      usersCache = {
        data: list,
        timestamp: Date.now(),
        userId,
        tenantId,
      };
      return list;
    })
    .catch((err) => {
      throw err;
    })
    .finally(() => {
      inFlightUsersPromise = null;
    });

  return inFlightUsersPromise;
};

export const getCachedUsers = (user) => {
  if (!user) return null;
  const { userId, tenantId } = extractIds(user);
  if (!userId) return null;
  const now = Date.now();
  if (
    usersCache.data &&
    usersCache.userId === userId &&
    usersCache.tenantId === tenantId &&
    now - usersCache.timestamp < TTL_MS
  ) {
    return usersCache.data;
  }
  return null;
};

export const invalidateUsersCache = () => {
  usersCache = { data: null, timestamp: 0, userId: null, tenantId: null };
  inFlightUsersPromise = null;
};

// ─── Departments Cache ───────────────────────────────────────────────────────
export const fetchDepartmentsCached = async (departmentAPI, user) => {
  if (!user || !departmentAPI?.getAll) return [];
  const { userId, tenantId } = extractIds(user);
  if (!userId) return [];

  const now = Date.now();
  if (
    deptsCache.data &&
    deptsCache.userId === userId &&
    deptsCache.tenantId === tenantId &&
    now - deptsCache.timestamp < TTL_MS
  ) {
    return deptsCache.data;
  }

  if (inFlightDeptsPromise) {
    return inFlightDeptsPromise;
  }

  inFlightDeptsPromise = departmentAPI.getAll()
    .then((res) => {
      const list = res?.data?.data?.departments || res?.data?.departments || [];
      deptsCache = {
        data: list,
        timestamp: Date.now(),
        userId,
        tenantId,
      };
      return list;
    })
    .catch((err) => {
      throw err;
    })
    .finally(() => {
      inFlightDeptsPromise = null;
    });

  return inFlightDeptsPromise;
};

export const getCachedDepartments = (user) => {
  if (!user) return null;
  const { userId, tenantId } = extractIds(user);
  if (!userId) return null;
  const now = Date.now();
  if (
    deptsCache.data &&
    deptsCache.userId === userId &&
    deptsCache.tenantId === tenantId &&
    now - deptsCache.timestamp < TTL_MS
  ) {
    return deptsCache.data;
  }
  return null;
};

export const invalidateDepartmentsCache = () => {
  deptsCache = { data: null, timestamp: 0, userId: null, tenantId: null };
  inFlightDeptsPromise = null;
};

export const clearAllLookupCache = () => {
  invalidateUsersCache();
  invalidateDepartmentsCache();
};
